import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Header from "../core/Header";
import Footer from "../core/Footer";
import { useParams } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { supabase } from "../utils/supabase";
import { useTranslation } from "../utils/i18n";

const TYPE_NORMALIZE = {
  News: "Nyhet",
  New: "Nyhet",
  Article: "Artikel",
  Information: "Information",
  Nyhet: "Nyhet",
  Artikel: "Artikel",
};

const CATEGORY_KEY_BY_TAG = {
  Nyhet: "nyhet",
  Artikel: "artikel",
  Information: "information",
};

export default function NewsDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [article, setArticle] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [fetchError, setFetchError] = React.useState("");
  const [lightboxOpen, setLightboxOpen] = React.useState(false);

  React.useEffect(() => {
    let isMounted = true;

    const fetchArticle = async () => {
      setIsLoading(true);
      setFetchError("");

      const { data, error } = await supabase
        .from("newsLetter")
        .select("id, created_at, title, description, type, image_url")
        .eq("id", id)
        .maybeSingle();

      if (!isMounted) return;

      if (error) {
        setFetchError(error.message || "Kunde inte hämta artikel.");
        setArticle(null);
      } else {
        setArticle(data || null);
      }

      setIsLoading(false);
    };

    fetchArticle();
    return () => {
      isMounted = false;
    };
  }, [id]);

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "#F6F7F9",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header />

      <Box
        sx={{
          flex: 1,
          width: "100%",
          maxWidth: 1440,
          mx: "auto",
          px: { xs: 2, sm: 3, md: 5 },
          pt: { xs: 10.5, md: 13 },
          pb: { xs: 5, md: 8 },
        }}
      >
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1.75 }}>
          <Link
            component={RouterLink}
            to="/news"
            underline="none"
            sx={{ color: "#4b4b4b" }}
          >
            {t("newsDetail.back")}
          </Link>
          {article && (
            <Typography aria-current="page" sx={{ color: "#FF6A00" }}>
              {article.title}
            </Typography>
          )}
        </Breadcrumbs>

        {fetchError ? (
          <Typography variant="h6" color="error" sx={{ mt: 4 }}>
            {fetchError}
          </Typography>
        ) : isLoading ? (
          <Typography color="text.secondary" sx={{ mt: 4 }}>
            {t("newsDetail.loading")}
          </Typography>
        ) : !article ? (
          <Typography variant="h6" sx={{ mt: 4 }}>
            {t("newsDetail.notFound")}
          </Typography>
        ) : (
          <Box sx={{ maxWidth: 860, mx: "auto", mt: 5 }}>
            {article.image_url && (
              <Box
                component="img"
                src={article.image_url}
                alt={article.title}
                onClick={() => setLightboxOpen(true)}
                sx={{
                  width: "100%",
                  objectFit: "contain",
                  borderRadius: 2,
                  display: "block",
                  mb: 4,
                  cursor: "pointer",
                }}
              />
            )}

            <Dialog
              open={lightboxOpen}
              onClose={() => setLightboxOpen(false)}
              fullScreen
              PaperProps={{ sx: { bgcolor: "rgba(0,0,0,0.75)" } }}
            >
              <IconButton
                onClick={() => setLightboxOpen(false)}
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  color: "#fff",
                  zIndex: 2,
                  bgcolor: "rgba(255,255,255,0.1)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                }}
              >
                <CloseIcon />
              </IconButton>

              <DialogContent
                onClick={() => setLightboxOpen(false)}
                sx={{
                  p: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    position: "relative",
                    display: "inline-flex",
                    maxWidth: "100%",
                    maxHeight: "100vh",
                  }}
                >
                  <Box
                    component="img"
                    src={article.image_url}
                    alt={article.title}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "100vh",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                </Box>
              </DialogContent>
            </Dialog>

            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
            >
              <Chip
                label={t(
                  `newsPage.categories.${
                    CATEGORY_KEY_BY_TAG[TYPE_NORMALIZE[article.type] || "Nyhet"]
                  }`,
                )}
                size="small"
                sx={{
                  bgcolor: "#FF6A00",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  borderRadius: 999,
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: "#9AA4B2", fontWeight: 500 }}
              >
                {t("newsDetail.published")}{" "}
                {article.created_at
                  ? new Date(article.created_at).toLocaleDateString("sv-SE", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })
                  : "-"}
              </Typography>
            </Box>

            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                letterSpacing: "-0.03em",
                lineHeight: 1.1,
                color: "#0B0F13",
                fontSize: { xs: "1.9rem", md: "2.6rem" },
                mb: 3,
              }}
            >
              {article.title}
            </Typography>

            <Box sx={{ borderTop: "1px solid rgba(30,26,24,0.12)", mb: 3 }} />

            <Typography
              variant="body1"
              sx={{
                color: "#374151",
                lineHeight: 1.9,
                fontSize: { xs: "1rem", md: "1.05rem" },
                whiteSpace: "pre-line",
              }}
            >
              {article.description || t("newsDetail.noDescription")}
            </Typography>
          </Box>
        )}
      </Box>

      <Footer />
    </Box>
  );
}
